import { prisma, MergeTag, Prisma } from "client";
import {
  getFormJsonKeysByEventId,
  getFormJsonKeysByContactBookGroupIds,
} from "../formTemplates";
// CREATE
export const createMergeTag = async (mergeTag: MergeTag) => {
  try {
    const newMergeTag = await prisma.mergeTag.create({
      data: mergeTag,
    });
    return { mergeTag: newMergeTag };
  } catch (error) {
    return { error };
  }
};

export const createManyMergeTags = async (mergeTags: MergeTag[]) => {
  await mergeTags.forEach(async (mergeTag) => {
    await prisma.mergeTag.create({
      data: {
        ...mergeTag,
      },
    });
  });
  return { mergeTags };
};

// READ
export const getAllMergeTags = async () => {
  try {
    const mergeTags = await prisma.mergeTag.findMany();
    return { mergeTags };
  } catch (error) {
    return { error };
  }
};

export const getMergeTagById = async (id: string) => {
  try {
    const mergeTag = await prisma.mergeTag.findUnique({
      where: { id },
    });
    return { mergeTag };
  } catch (error) {
    return { error };
  }
};

export const getMergeTagsByMergeTagGroupId = async (groupId: string) => {
  try {
    const mergeTags = await prisma.mergeTag.findMany({
      where: { mergeTagGroupId: groupId },
    });
    return { mergeTags };
  } catch (error) {
    return { error };
  }
};

export const getMergeTagsByMergeTagGroupName = async (groupName: string) => {
  try {
    const mergeTagGroup = await prisma.mergeTagGroup.findUnique({
      where: { name: groupName },
    });
    const mergeTags = mergeTagGroup
      ? await prisma.mergeTag.findMany({
          where: { mergeTagGroupId: mergeTagGroup.id },
        })
      : [];
    return { mergeTags };
  } catch (error) {
    return { error };
  }
};

// UPDATE
export const updateMergeTag = async (id: string, mergeTag: MergeTag) => {
  try {
    const updatedMergeTag = await prisma.mergeTag.update({
      where: { id },
      data: mergeTag,
    });
    return { mergeTag: updatedMergeTag };
  } catch (error) {
    return { error };
  }
};

// DELETE
export const deleteMergeTag = async (id: string) => {
  try {
    const deletedMergeTag = await prisma.mergeTag.delete({
      where: { id },
    });
    return { mergeTag: deletedMergeTag };
  } catch (error) {
    return { error };
  }
};

// delete all merge tags by merge tag group id
export const deleteMergeTagsByMergeTagGroupId = async (groupId: string) => {
  try {
    const deletedMergeTags = await prisma.mergeTag.deleteMany({
      where: { mergeTagGroupId: groupId },
    });
    return { mergeTags: deletedMergeTags };
  } catch (error) {
    return { error };
  }
};

// delete all
export const deleteAllMergeTags = async () => {
  try {
    const deletedMergeTags = await prisma.mergeTag.deleteMany();
    return { mergeTags: deletedMergeTags };
  } catch (error) {
    return { error };
  }
};

export const getModelAndTranslationKeys = async (eventId?: string) => {
  const selectedModels = [
    "Ticket",
    "Event",
    "Order",
    "User",
    "TicketCategory",
    "TicketGroup",
    "OTP",
    "Organization",
    "WhiteLabel",
    "Exhibitor",
    "ParticipantGroup",
    "Task",
    "OrderItem",
    "Payment",
    "Invoice",
    "Invitation",
    // Add more models and their translation counterparts here
  ];

  const supportedLangs = ["ar", "en"]; // You can expand this if needed
  try {
    const models = Prisma.dmmf.datamodel.models;
    const schemaKeys: { [key: string]: string[] } = {};

    const filteredModels = models.filter((model) =>
      selectedModels.includes(model.name)
    );

    for (const model of filteredModels) {
      const modelName = model.name;

      // Get the main model's fields
      const modelFields = model.fields.map((field) => field.name);

      schemaKeys[modelName] = [...modelFields];

      // Check if the model has a translation relation
      const translationField = model.fields.find(
        (field) =>
          field.relationName &&
          field.relationName.toLowerCase().includes("translations")
      );

      if (translationField) {
        // Find the related translation model
        const relatedModel = models.find(
          (mod) => mod.name === translationField.type
        );

        if (relatedModel) {
          const translationFieldNames = relatedModel.fields
            .map((field) => field.name)
            .filter(
              (name) =>
                name !== "id" &&
                name !== "language" &&
                name !==
                  `${modelName.charAt(0).toLowerCase()}${modelName.slice(1)}Id`
            );

          const expandedTranslations = translationFieldNames.flatMap((key) =>
            supportedLangs.map((lang) => `${key}_${lang}`)
          );

          schemaKeys[modelName] = [...modelFields, ...expandedTranslations];
        }
      }
    }

    if (eventId) {
      const formJsonKeys = await getFormJsonKeysByEventId(eventId);
      schemaKeys["Form"] = [...formJsonKeys];
      const contactBookListKeys = await getFormJsonKeysByContactBookGroupIds(
        eventId
      );
      schemaKeys["user"] = [...contactBookListKeys];
    }

    // Add custom merge tags that are not in the schema
    const customMergeTags: { [key: string]: string[] } = {
      Invitation: [
        "response_url",
        "yes_response_url",
        "no_response_url",
        "maybe_response_url",
        "response_yes",
        "response_no",
        "response_maybe",
        "ticket_registration_page",
        "public_invitation_page",
        "yes",
        "no",
        "maybe",
        "link",
      ],
    };

    // Merge custom merge tags with schema keys
    Object.entries(customMergeTags).forEach(([model, tags]) => {
      if (schemaKeys[model]) {
        schemaKeys[model] = [...new Set([...schemaKeys[model], ...tags])];
      } else {
        schemaKeys[model] = [...tags];
      }
    });

    return schemaKeys;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
